import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  CreateProductCategoryFormType,
  CreateProductCategoryResponse,
  CreateProductSubCategoryFormType,
  CreateProductSubCategoryResponse,
  EditProductCategoryFormType,
  EditProductCategoryResponse,
  EditProductSubCategoryFormType,
  EditProductSubCategoryResponse,
  ProductCategory,
  ProductCategoryFormType,
  ProductSubCategory,
  ProductSubCategoryFormType,
} from "../api/interfaces";
import {
  useCreateProductCategory,
  useCreateProductSubCategory,
  useEditProductCategory,
  useEditProductSubCategory,
} from "../api/mutations";
import { useGetProductCategory } from "../api/queries";
import {
  productCategoryFormSchema,
  productSubCategoryFormSchema,
} from "../productCategorySchema";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";

interface HandleSubmitCreateProps {
  form: UseFormReturn<CreateProductCategoryFormType, any, any>;
  mutation: UseMutationResult<
    CreateProductCategoryResponse,
    Error,
    CreateProductCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitCreateProductSubCategoryProps {
  productCategoryId: number;
  form: UseFormReturn<ProductSubCategoryFormType, any, any>;
  mutation: UseMutationResult<
    CreateProductSubCategoryResponse,
    Error,
    CreateProductSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditProps {
  id: string;
  form: UseFormReturn<ProductCategoryFormType, any, undefined>;
  mutation: UseMutationResult<
    EditProductCategoryResponse,
    Error,
    EditProductCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

interface HandleSubmitEditProductSubCategoryProps {
  id: string;
  productCategoryId: number;
  form: UseFormReturn<ProductSubCategoryFormType, any, undefined>;
  mutation: UseMutationResult<
    EditProductSubCategoryResponse,
    Error,
    EditProductSubCategoryFormType,
    unknown
  >;
  handleClose: () => void;
}

export const handleSubmitCreateProductCategory = async ({
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateProps) => {
  const values = form.getValues();

  const payload = {
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

export const handleSubmitCreateProductSubCategory = async ({
  productCategoryId,
  form,
  mutation,
  handleClose,
}: HandleSubmitCreateProductSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    productCategoryId,
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEdit = async ({
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditProps) => {
  const values = form.getValues();

  const payload = {
    id,
    name: values.name,
    description: values.description,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const handleSubmitEditProductSubCategory = async ({
  productCategoryId,
  id,
  form,
  mutation,
  handleClose,
}: HandleSubmitEditProductSubCategoryProps) => {
  const values = form.getValues();

  const payload = {
    id,
    productCategoryId,
    name: values.name,
    color: values.color,
  };

  try {
    await mutation.mutateAsync(payload);

    handleClose();
  } catch (error) {
    apiErrorHandler({ error, form });
  }
};

const useProductCategoryHandler = () => {
  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["Master Data - Product Category"],
    action: ActionType["Create"],
  });

  const [isShowFormModal, setIsShowFormModal] = useState(false);
  const [
    isShowProductSubCategoryFormModal,
    setIsShowProductSubCategoryFormModal,
  ] = useState(false);

  const [selectedData, setSelectedData] = useState<
    ProductCategory | ProductSubCategory | null
  >(null);
  const [selectedDataParentId, setSelectedDataParentId] = useState<
    number | null
  >(null);

  const { data, isLoading } = useGetProductCategory();

  const form = useForm<z.infer<typeof productCategoryFormSchema>>({
    resolver: zodResolver(productCategoryFormSchema),
  });
  const productSubCategoryForm = useForm<
    z.infer<typeof productSubCategoryFormSchema>
  >({
    resolver: zodResolver(productSubCategoryFormSchema),
  });

  const createMutation = useCreateProductCategory();
  const editMutation = useEditProductCategory();

  const createProductSubCategoryMutation = useCreateProductSubCategory();
  const editProductSubCategoryMutation = useEditProductSubCategory();

  const handleAdd = () => setIsShowFormModal(true);
  const handleCloseAdd = () => {
    setIsShowFormModal(false);
    form.reset({ name: "", description: "" });
  };

  const handleEdit = (data: ProductCategory) => {
    setIsShowFormModal(true);
    setSelectedData(data);
    form.reset({
      name: data?.name,
      description: data.description ?? "",
    });
  };
  const handleCloseEdit = () => {
    setIsShowFormModal(false);
    setSelectedData(null);
    form.reset({ name: "", description: "" });
  };

  const handleAddProductSubCategory = (parentId: number) => {
    setSelectedDataParentId(parentId);
    setIsShowProductSubCategoryFormModal(true);
  };
  const handleCloseAddProductSubCategory = () => {
    setSelectedDataParentId(null);
    setIsShowProductSubCategoryFormModal(false);
    productSubCategoryForm.reset({ name: "", color: "" });
  };

  const handleEditProductSubCategory = (data: ProductSubCategory) => {
    setIsShowProductSubCategoryFormModal(true);
    setSelectedData(data);
    setSelectedDataParentId(data.product_category_id);
    productSubCategoryForm.reset({
      name: data?.name,
      color: data?.hex || `#FFFFFF`,
    });
  };
  const handleCloseEditProductSubCategory = () => {
    setIsShowProductSubCategoryFormModal(false);
    setSelectedData(null);
    setSelectedDataParentId(null);
    productSubCategoryForm.reset({ name: "", color: "" });
  };

  return {
    isLoading,
    isAllowToCreate,
    productCategory: data?.data,
    isShowFormModal,
    isShowProductSubCategoryFormModal,
    handleAdd,
    handleEdit,
    handleAddProductSubCategory,
    handleEditProductSubCategory,
    form,
    productSubCategoryForm,
    formType: (!selectedData ? "CREATE" : "EDIT") as "CREATE" | "EDIT",
    handleCancelFormModal: () =>
      !selectedData ? handleCloseAdd() : handleCloseEdit(),
    handleSubmitFormModal: form.handleSubmit(
      async () =>
        await (!selectedData
          ? handleSubmitCreateProductCategory({
              form,
              mutation: createMutation,
              handleClose: handleCloseAdd,
            })
          : handleSubmitEdit({
              id: `${selectedData?.id}`,
              form,
              mutation: editMutation,
              handleClose: handleCloseEdit,
            }))
    ),
    handleCancelProductSubCategoryFormModal: () =>
      !selectedData
        ? handleCloseAddProductSubCategory()
        : handleCloseEditProductSubCategory(),
    handleSubmitProductSubCategoryFormModal:
      productSubCategoryForm.handleSubmit(
        async () =>
          await (!selectedData
            ? handleSubmitCreateProductSubCategory({
                productCategoryId: selectedDataParentId as number,
                form: productSubCategoryForm,
                mutation: createProductSubCategoryMutation,
                handleClose: handleCloseAddProductSubCategory,
              })
            : handleSubmitEditProductSubCategory({
                id: `${selectedData?.id}`,
                productCategoryId: selectedDataParentId as number,
                form: productSubCategoryForm,
                mutation: editProductSubCategoryMutation,
                handleClose: handleCloseEditProductSubCategory,
              }))
      ),
  };
};

export default useProductCategoryHandler;
